import { readTillBusinessId } from './tillStore';
import { AVATAR_TINTS } from './login.theme';

/**
 * The waiter tiles pinned to this computer.
 *
 * A tile is a SHORTCUT, not a stored login: it holds the waiter's id, name and
 * username so the screen can draw them, and nothing else. The password is
 * asked for on every single sign-in and is never written here.
 *
 * Scoped by business, because a till belongs to one restaurant and the login
 * screen must not leak another business's staff list.
 */

const KEY = 'dalabtire.waiterProfiles.v1';

function readAll() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (err) {
    return {};
  }
}

function writeAll(byBusiness) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(byBusiness));
  } catch (err) {
    /* storage unavailable — tiles just don't stick */
  }
}

/**
 * The colour a new waiter gets: whichever is used least on this till, earliest
 * index winning a tie. With eight tints and a normal-sized floor staff that
 * means every tile is a different colour; past eight it starts doubling up,
 * evenly.
 */
function leastUsedTint(list) {
  const counts = AVATAR_TINTS.map(() => 0);
  list.forEach((profile) => {
    if (typeof profile.tintIndex === 'number' && counts[profile.tintIndex] !== undefined) {
      counts[profile.tintIndex] += 1;
    }
  });
  return counts.indexOf(Math.min.apply(null, counts));
}

/** Most recently used first: the person who just finished is usually next. */
function byRecency(a, b) {
  return (b.lastUsedAt || b.addedAt || 0) - (a.lastUsedAt || a.addedAt || 0);
}

/**
 * @param {string} [businessId] defaults to the business this till last saw
 * @returns {Array<{id: string, name: string, username: string, addedAt: number, lastUsedAt: number}>}
 */
export function readProfiles(businessId) {
  const scope = businessId || readTillBusinessId();
  if (!scope) return [];
  const list = readAll()[scope];
  return Array.isArray(list) ? list.slice().sort(byRecency) : [];
}

/**
 * Pin a waiter to this till. Adding someone already pinned refreshes their
 * name rather than making a second tile.
 */
export function addProfile(businessId, { id, name, username }) {
  if (!businessId || !id) return readProfiles(businessId);

  const all = readAll();
  const list = Array.isArray(all[businessId]) ? all[businessId] : [];
  const now = Date.now();
  const existing = list.find((p) => p.id === id);

  const next = existing
    ? // Re-adding refreshes the name but never the colour — the whole point of
      // a colour is that it stays the same person's.
      list.map((p) => (p.id === id ? { ...p, name, username, lastUsedAt: now } : p))
    : list.concat([
        {
          id,
          name,
          username,
          tintIndex: leastUsedTint(list),
          addedAt: now,
          lastUsedAt: now,
        },
      ]);

  all[businessId] = next;
  writeAll(all);
  return next.slice().sort(byRecency);
}

export function removeProfile(businessId, id) {
  if (!businessId) return [];
  const all = readAll();
  const list = Array.isArray(all[businessId]) ? all[businessId] : [];
  all[businessId] = list.filter((p) => p.id !== id);
  writeAll(all);
  return all[businessId].slice().sort(byRecency);
}

/** Called after a successful unlock, so the tile floats to the front. */
export function touchProfile(businessId, id) {
  if (!businessId) return [];
  const all = readAll();
  const list = Array.isArray(all[businessId]) ? all[businessId] : [];
  all[businessId] = list.map((p) => (p.id === id ? { ...p, lastUsedAt: Date.now() } : p));
  writeAll(all);
  return all[businessId].slice().sort(byRecency);
}
